import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Form, InputGroup } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { collection, getDocs, query, orderBy, doc, updateDoc, getCountFromServer, limit, startAfter, startAt, endAt } from 'firebase/firestore';
import { db } from '../firebase';
import Filter from './Filter';
import NextPrevPagination from './NextPrevPagination';

const UsersTable = () => {
    const [users, setUsers] = useState([]);
    const [order, setOrder] = useState('desc');
    const [localSearch, setLocalSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [pageDocs, setPageDocs] = useState([null]);
    const [totalCount, setTotalCount] = useState(0);
    const itemsPerPage = 20;

    // Debounce search input to avoid excessive Firestore reads
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchQuery(localSearch);
        }, 500);
        return () => clearTimeout(handler);
    }, [localSearch]);

    const fetchTotalCount = async () => {
        try {
            const rawVal = searchQuery.trim();
            const qVal = rawVal.toLowerCase();
            if (!qVal) {
                const q = query(collection(db, 'users'));
                const snapshot = await getCountFromServer(q);
                setTotalCount(snapshot.data().count);
            } else if (qVal.includes('@')) {
                const q = query(collection(db, 'users'), orderBy('email'), startAt(qVal), endAt(qVal + '\uf8ff'));
                const snapshot = await getCountFromServer(q);
                setTotalCount(snapshot.data().count);
            } else if (/^\+?\d+$/.test(qVal)) {
                const phoneWithPlus = qVal.startsWith('+') ? qVal : '+' + qVal;
                const phoneWithoutPlus = qVal.startsWith('+') ? qVal.slice(1) : qVal;
                const [withPlusSnap, withoutPlusSnap] = await Promise.all([
                    getCountFromServer(query(collection(db, 'users'), orderBy('phoneNumber'), startAt(phoneWithPlus), endAt(phoneWithPlus + '\uf8ff'))),
                    getCountFromServer(query(collection(db, 'users'), orderBy('phoneNumber'), startAt(phoneWithoutPlus), endAt(phoneWithoutPlus + '\uf8ff')))
                ]);
                setTotalCount(withPlusSnap.data().count + withoutPlusSnap.data().count);
            } else {
                const [emailCountSnap, uidCountSnap] = await Promise.all([
                    getCountFromServer(query(collection(db, 'users'), orderBy('email'), startAt(qVal), endAt(qVal + '\uf8ff'))),
                    getCountFromServer(query(collection(db, 'users'), orderBy('uid'), startAt(rawVal), endAt(rawVal + '\uf8ff')))
                ]);
                setTotalCount(emailCountSnap.data().count + uidCountSnap.data().count);
            }
        } catch (error) {
            console.error('Error fetching users count:', error);
            setTotalCount(0);
        }
    };

    const fetchPage = async (pageNumber, startDoc) => {
        try {
            setLoading(true);
            const rawVal = searchQuery.trim();
            const qVal = rawVal.toLowerCase();

            if (!qVal) {
                const constraints = [orderBy('createdAt', order)];
                if (startDoc) {
                    constraints.push(startAfter(startDoc));
                }
                constraints.push(limit(itemsPerPage));

                const q = query(collection(db, 'users'), ...constraints);
                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(docSnap => ({ ...docSnap.data(), docId: docSnap.id }));
                setUsers(data);

                const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
                setPageDocs(prev => {
                    const next = [...prev];
                    next[pageNumber] = lastDoc;
                    return next;
                });
            } else {
                if (qVal.includes('@')) {
                    const constraints = [orderBy('email'), startAt(qVal), endAt(qVal + '\uf8ff')];
                    if (startDoc) constraints.push(startAfter(startDoc));
                    constraints.push(limit(itemsPerPage));

                    const q = query(collection(db, 'users'), ...constraints);
                    const querySnapshot = await getDocs(q);
                    const data = querySnapshot.docs.map(docSnap => ({ ...docSnap.data(), docId: docSnap.id }));
                    setUsers(data);

                    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
                    setPageDocs(prev => {
                        const next = [...prev];
                        next[pageNumber] = lastDoc;
                        return next;
                    });
                } else if (/^\+?\d+$/.test(qVal)) {
                    const phoneWithPlus = qVal.startsWith('+') ? qVal : '+' + qVal;
                    const phoneWithoutPlus = qVal.startsWith('+') ? qVal.slice(1) : qVal;

                    const phoneWithPlusLast = startDoc?.phoneWithPlusLast || null;
                    const phoneWithoutPlusLast = startDoc?.phoneWithoutPlusLast || null;

                    const withPlusConstraints = [orderBy('phoneNumber'), startAt(phoneWithPlus), endAt(phoneWithPlus + '\uf8ff')];
                    if (phoneWithPlusLast) withPlusConstraints.push(startAfter(phoneWithPlusLast));
                    withPlusConstraints.push(limit(itemsPerPage));

                    const withoutPlusConstraints = [orderBy('phoneNumber'), startAt(phoneWithoutPlus), endAt(phoneWithoutPlus + '\uf8ff')];
                    if (phoneWithoutPlusLast) withoutPlusConstraints.push(startAfter(phoneWithoutPlusLast));
                    withoutPlusConstraints.push(limit(itemsPerPage));

                    const [withPlusSnap, withoutPlusSnap] = await Promise.all([
                        getDocs(query(collection(db, 'users'), ...withPlusConstraints)),
                        getDocs(query(collection(db, 'users'), ...withoutPlusConstraints))
                    ]);

                    const mergedMap = new Map();
                    withPlusSnap.docs.forEach(d => mergedMap.set(d.id, d));
                    withoutPlusSnap.docs.forEach(d => mergedMap.set(d.id, d));

                    const mergedDocs = Array.from(mergedMap.values());
                    mergedDocs.sort((a, b) => {
                        const timeA = a.data().createdAt?.seconds || 0;
                        const timeB = b.data().createdAt?.seconds || 0;
                        return order === 'desc' ? timeB - timeA : timeA - timeB;
                    });

                    const finalDocs = mergedDocs.slice(0, itemsPerPage);
                    const data = finalDocs.map(docSnap => ({ ...docSnap.data(), docId: docSnap.id }));
                    setUsers(data);

                    const lastWithPlusDoc = withPlusSnap.docs[withPlusSnap.docs.length - 1] || null;
                    const lastWithoutPlusDoc = withoutPlusSnap.docs[withoutPlusSnap.docs.length - 1] || null;

                    setPageDocs(prev => {
                        const next = [...prev];
                        next[pageNumber] = { phoneWithPlusLast: lastWithPlusDoc, phoneWithoutPlusLast: lastWithoutPlusDoc };
                        return next;
                    });
                } else {
                    const emailLast = startDoc?.emailLast || null;
                    const uidLast = startDoc?.uidLast || null;

                    const emailConstraints = [orderBy('email'), startAt(qVal), endAt(qVal + '\uf8ff')];
                    if (emailLast) emailConstraints.push(startAfter(emailLast));
                    emailConstraints.push(limit(itemsPerPage));

                    const uidConstraints = [orderBy('uid'), startAt(rawVal), endAt(rawVal + '\uf8ff')];
                    if (uidLast) uidConstraints.push(startAfter(uidLast));
                    uidConstraints.push(limit(itemsPerPage));

                    const [emailSnap, uidSnap] = await Promise.all([
                        getDocs(query(collection(db, 'users'), ...emailConstraints)),
                        getDocs(query(collection(db, 'users'), ...uidConstraints))
                    ]);

                    const mergedMap = new Map();
                    emailSnap.docs.forEach(d => mergedMap.set(d.id, d));
                    uidSnap.docs.forEach(d => mergedMap.set(d.id, d));

                    const mergedDocs = Array.from(mergedMap.values());
                    mergedDocs.sort((a, b) => {
                        const timeA = a.data().createdAt?.seconds || 0;
                        const timeB = b.data().createdAt?.seconds || 0;
                        return order === 'desc' ? timeB - timeA : timeA - timeB;
                    });

                    const finalDocs = mergedDocs.slice(0, itemsPerPage);
                    const data = finalDocs.map(docSnap => ({ ...docSnap.data(), docId: docSnap.id }));
                    setUsers(data);

                    const lastEmailDoc = emailSnap.docs[emailSnap.docs.length - 1] || null;
                    const lastUidDoc = uidSnap.docs[uidSnap.docs.length - 1] || null;

                    setPageDocs(prev => {
                        const next = [...prev];
                        next[pageNumber] = { emailLast: lastEmailDoc, uidLast: lastUidDoc };
                        return next;
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching users page:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initFetch = async () => {
            setPageDocs([null]);
            setCurrentPage(1);
            await fetchTotalCount();
            await fetchPage(1, null);
        };

        initFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order, searchQuery]);

    const handleFilterChange = (e) => {
        setOrder(e.target.value);
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            const prevPage = currentPage - 1;
            setCurrentPage(prevPage);
            fetchPage(prevPage, pageDocs[prevPage - 1]);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchPage(nextPage, pageDocs[nextPage - 1]);
        }
    };

    const handleStatusChange = async (docId, newStatus) => {
        try {
            const userRef = doc(db, "users", docId);
            await updateDoc(userRef, { status: newStatus });
            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    user.docId === docId ? { ...user, status: newStatus } : user
                )
            );
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

    return (
        <section className="px-1 py-4 nav-body-bg btm-rad">
            <Container>
                <Row className="align-items-center justify-content-between flex-column flex-md-row text-center text-md-start">
                    <Col xs="auto" className="mb-3 mb-md-0">
                        <h3 className="text-h2">All Users</h3>
                    </Col>

                    <Col md={4}>
                        <InputGroup className="theme-search-group">
                            <Form.Control
                                type="text"
                                placeholder="Search email, phone or user ID"
                                className="db-set-style border-0 shadow-none"
                                style={{ backgroundColor: 'transparent' }}
                                value={localSearch}
                                onChange={(e) => setLocalSearch(e.target.value)}
                            />
                            <InputGroup.Text className="bg-transparent border-0">
                                <FaSearch style={{ color: 'var(--accent-lime)' }} />
                            </InputGroup.Text>
                        </InputGroup>
                    </Col>

                    <Col xs="auto">
                        <Filter value={order} onChange={handleFilterChange} />
                    </Col>
                </Row>

                <div className="table-responsive scrollable-table-y mt-4" style={{ minHeight: '100vh', overflowY: 'auto' }}>
                    <style>
                        {`
                        @media (max-width: 768px) {
                            .scrollable-table-y {
                                max-height: 300px;
                                overflow-y: auto;
                            }
                        }
                        `}
                    </style>
                    <Table className="table-hover align-middle body-text-two mb-0">
                        <thead className="sticky-top top-0" style={{ zIndex: 10, background: 'transparent !important' }}>
                            <tr>
                                <th className="transparent-header">User ID</th>
                                <th className="text-center transparent-header">Name</th>
                                <th className="text-center transparent-header">Phone</th>
                                <th className="text-center transparent-header">Email</th>
                                <th className="text-center transparent-header">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-4 transparent-header">
                                        <div className="admin-loader"></div>
                                    </td>
                                </tr>
                            ) : users.length > 0 ? (
                                users.map((item, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid var(--divider)' }}>
                                        <td className="py-3 transparent-header">{item.uid}</td>
                                        <td className="py-3 transparent-header text-start">{item.name}</td>
                                        <td className="py-3 transparent-header text-center">{item.phoneNumber}</td>
                                        <td className="py-3 transparent-header text-start">{item.email}</td>
                                        <td className="py-3 transparent-header text-center">
                                            <Form.Select
                                                value={item.status || "Active"}
                                                onChange={(e) => handleStatusChange(item.docId, e.target.value)}
                                                size="sm"
                                                className={`transparent-header border-0 ${(item.status || "Active") === "Active" ? "text-success" : "text-danger"}`}
                                                style={{ width: "100px", margin: "0 auto" }}
                                            >
                                                <option value="Active" className="text-dark">Active</option>
                                                <option value="Inactive" className="text-dark">Inactive</option>
                                            </Form.Select>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-4 transparent-header">
                                        No users found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                <NextPrevPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPrev={handlePrev}
                    onNext={handleNext}
                />
            </Container>
        </section>
    );
};

export default UsersTable;
